import axios from 'axios';
import { playButtonAlertStreamAudio, isStreamAlertAudioPlaying } from './alarmSound';
import {
  pollAndPlayButtonAlertStreamAudio,
  streamAudioReady,
  streamAudioVersionFromRow,
  type AlertRow,
} from './buttonAlertStreamPlayback';
import { notificationPayloadToNum } from './notificationPayload';
import { isValidLinkedSiteId } from './buttonAlertSiteLink';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

const inFlight = new Map<number, AbortController>();

/**
 * id de alerta -> última versión de MP3 (metadata.stream_audio_version) que ya sonó.
 * El backend reutiliza la misma alerta abierta para audios sucesivos del mismo
 * stream, así que dedupear solo por id impedía reproducir los audios nuevos.
 * Con la versión, cada MP3 nuevo se reproduce una vez sin repetir el anterior.
 */
const lastPlayedAudioVersion = new Map<number, number>();

function markStreamAudioPlayed(buttonAlertId: number, version: number | undefined): void {
  lastPlayedAudioVersion.set(buttonAlertId, version ?? Date.now());
}

/**
 * true si ese audio exacto ya se reprodujo. Con `version` conocido bloquea solo
 * el mismo MP3 (permite re-sonar audios nuevos). Sin versión (notificación
 * play-sound previa al finalize) bloquea si ya sonó algo para esa alerta.
 */
function hasPlayedStreamAudio(buttonAlertId: number, version: number | undefined): boolean {
  const last = lastPlayedAudioVersion.get(buttonAlertId);
  if (last == null) return false;
  if (version == null) return true;
  return last === version;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const t = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(t);
      signal.removeEventListener('abort', onAbort);
      resolve();
    };
    signal.addEventListener('abort', onAbort);
  });
}

function isPersonalizedAlertRow(row: Record<string, unknown>): boolean {
  const meta = row.metadata;
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return false;
  const m = meta as Record<string, unknown>;
  const sound = notificationPayloadToNum(m.sound);
  if (sound === 14) return true;
  return m.stream_id != null && String(m.stream_id).trim() !== '';
}

/**
 * Reproduce el MP3 del stream en cuanto exista en BD (sonido 14).
 * Independiente de la sirena. No se cancela por notificaciones play-sound previas.
 */
export function scheduleStreamAlertPlayback(
  buttonAlertId: number,
  token: string,
  options?: { immediate?: boolean; force?: boolean; version?: number }
): void {
  if (!Number.isFinite(buttonAlertId) || buttonAlertId < 1 || !token.trim()) return;

  const immediate = options?.immediate === true;
  const force = options?.force === true;
  const notifVersion = options?.version;

  // Solo se bloquea el MISMO audio ya reproducido (por versión). Una re-notificación
  // del mismo MP3 (p. ej. el evento stream-finalized que sigue al play-sound inicial)
  // no vuelve a sonar, pero un audio NUEVO de la misma alerta (backend reutiliza la
  // alerta abierta) sí se reproduce sin necesidad de una nueva pulsación.
  if (hasPlayedStreamAudio(buttonAlertId, notifVersion)) {
    return;
  }

  const prev = inFlight.get(buttonAlertId);
  if (prev) {
    if (!immediate && !force) return;
    prev.abort();
  }

  const ac = new AbortController();
  inFlight.set(buttonAlertId, ac);

  void (async () => {
    try {
      if (immediate) {
        for (let attempt = 0; attempt < 12 && !ac.signal.aborted; attempt++) {
          try {
            const res = await axios.get<{ success?: boolean; data?: AlertRow }>(
              `${API_URL}/button-alerts/${buttonAlertId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                signal: ac.signal,
              }
            );
            const row = res.data?.data;
            if (streamAudioReady(row)) {
              const version = streamAudioVersionFromRow(row) ?? notifVersion;
              if (hasPlayedStreamAudio(buttonAlertId, version)) {
                return;
              }
              const played = await playButtonAlertStreamAudio(buttonAlertId, token, { force });
              if (played) {
                markStreamAudioPlayed(buttonAlertId, version);
                return;
              }
              // Sin force: otro stream suena; no seguir spameando ni cortar.
              if (!force && isStreamAlertAudioPlaying()) {
                return;
              }
            }
          } catch (e) {
            if (axios.isCancel(e) || ac.signal.aborted) return;
          }
          await sleep(280, ac.signal);
        }
      }

      if (ac.signal.aborted) return;
      await pollAndPlayButtonAlertStreamAudio(buttonAlertId, token, ac.signal, {
        force,
        isVersionPlayed: (v) => hasPlayedStreamAudio(buttonAlertId, v),
        onPlayed: (v) => markStreamAudioPlayed(buttonAlertId, v),
      });
    } finally {
      if (inFlight.get(buttonAlertId) === ac) {
        inFlight.delete(buttonAlertId);
      }
    }
  })();
}

/**
 * Tras socket/campana: busca alertas 14 pendientes aún no reproducidas.
 * Nunca usa force (evita cortar el MP3 actual por alertas viejas sin audio o ya listas).
 * Prioriza la más reciente lista; el resto solo se programa si no hay audio de stream sonando.
 */
export async function scanPendingPersonalizedStreamAlerts(token: string): Promise<void> {
  try {
    const res = await axios.get<{ success?: boolean; data?: unknown[] }>(
      `${API_URL}/button-alerts/pending`,
      { headers: { Authorization: `Bearer ${token}` }, params: { limit: 80 } }
    );
    const rows = res.data?.data;
    if (!Array.isArray(rows)) return;

    type Candidate = { id: number; ready: boolean; version: number | undefined };
    const candidates: Candidate[] = [];

    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      const r = row as Record<string, unknown>;
      if (!isPersonalizedAlertRow(r)) continue;
      const id = notificationPayloadToNum(r.id);
      if (id == null || id < 1) continue;
      if (!isValidLinkedSiteId(r.siteId ?? r.site_id)) continue;

      const meta =
        r.metadata && typeof r.metadata === 'object' && !Array.isArray(r.metadata)
          ? (r.metadata as Record<string, unknown>)
          : {};
      const version = notificationPayloadToNum(meta.stream_audio_version);
      // Saltar solo si este audio exacto ya sonó (permite audios nuevos de la misma alerta).
      if (hasPlayedStreamAudio(id, version)) continue;
      const ready =
        meta.stream_finalized === true &&
        r.audioPath != null &&
        String(r.audioPath).trim() !== '';

      candidates.push({ id, ready, version });
    }

    if (candidates.length === 0) return;

    // Más reciente primero (ids autoincrementales).
    candidates.sort((a, b) => b.id - a.id);

    if (isStreamAlertAudioPlaying()) {
      // No interrumpir: solo dejar pendiente la más nueva no lista (poll silencioso).
      const newestNotReady = candidates.find((c) => !c.ready);
      if (newestNotReady) {
        scheduleStreamAlertPlayback(newestNotReady.id, token, {
          immediate: false,
          force: false,
          version: newestNotReady.version,
        });
      }
      return;
    }

    const newestReady = candidates.find((c) => c.ready);
    if (newestReady) {
      scheduleStreamAlertPlayback(newestReady.id, token, {
        immediate: true,
        force: false,
        version: newestReady.version,
      });
      return;
    }

    scheduleStreamAlertPlayback(candidates[0].id, token, {
      immediate: false,
      force: false,
      version: candidates[0].version,
    });
  } catch {
    /* ignore */
  }
}
